// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import styles from './OwnCaptureBand.module.css'
import CategoryQuestion from './CategoryQuestion'
import DeclaredStrip from './DeclaredStrip'
import OwnNoteCard from './OwnNoteCard'
import { useNoteBand } from './useNoteBand'
import { publicConfig } from 'config'
import { brandedTitle } from 'lib/dealhunt/url'
import type {
  DealHuntSuggestHit,
  OwnerCardOwners,
  OwnerClaimResponse,
  OwnerPublishResponse,
} from 'lib/dealhunt/types'
import { useOnScreenOnce, useOwnerTrack } from 'lib/owner-notes/analytics'
import { turnstileConfigured } from 'lib/owner-notes/turnstile'
import { useDeclaredProduct } from 'lib/owner-notes/declared'
import {
  declaredFromSeed,
  prefetchDeclaration,
  seedFromHit,
  useDeclaredOwners,
  usePendingDeclare,
} from 'lib/owner-notes/declaration'
import { ownViewsFromMe, useOwnNote } from 'lib/owner-notes/own-note'
import { useWriterHandoff } from 'lib/community/use-writer-handoff'
import { SignInGateContext, signInGateFor } from 'lib/owner-notes/signin-gate'
import { bandDraftKey } from 'lib/owner-notes/draft-waiting'
import { useOwnTypeahead } from 'lib/owner-notes/own-typeahead'
import type { DeltaNumbers } from 'lib/owner-notes/deltas'
import {
  CATEGORY_STRIP,
  categoryNounOf,
  categoryStripBody,
  GATED_GUIDE_COPY,
  GUIDE_COPY,
  ownerAskVisible,
  ownersFromCard,
  ownStatusLabel,
  SWITCH_LABEL,
  switchLine,
  UNDECLARE_LABEL,
} from 'lib/owner-notes/band-copy'

/** What the category grid knows about a product, keyed by node id (= v3 card objectID). */
export interface GridNumbers extends DeltaNumbers {
  slug: string | null
  name: string
  imageUrl: string | null
  /** The card's owner counts (#66) — the band's payoff line before the form. */
  cardOwners?: OwnerCardOwners | null
}

interface OwnCaptureBandProps {
  categoryName: string
  /** DealHunt's category noun (`summary.noun`, acronym-safe); falls back to
   *  `categoryName` lowercased when absent. */
  categoryNoun?: string
  /** Numbers for the products currently in the grid — the richest source for the
   *  declared product (a suggest hit carries price + SG score only). */
  gridNumbers: ReadonlyMap<string, GridNumbers>
  /** The page can ask the category's owners a question (`asksQuestions`): its node id and
   *  DealHunt fullSlug. Absent = no question row (a node DealHunt files no question under). */
  questionCategory?: { nodeId: string; fullSlug: string } | null
}

/**
 * "Own one already?" — the category-page capture band (contract §1, #63, #65, §16.3).
 * The reader names the product they own (typeahead over the same index as the header
 * search); the page remembers it (localStorage) and every card prints ± deltas against
 * it; then — value first, note after — the page offers the owner note on that product.
 * No handle step. Server-rendered as the ask only; everything visitor-specific hydrates in.
 * Once a pair is declared the ask becomes the reference strip, in place (2026-09-18): the
 * best-list article's own declared state (`DeclaredStrip`) — the pair, its fingerprint, the
 * switch, "Would you buy it again?" — and the form opens under it. It replaced proposal C
 * (2026-09-08: a chip beside Sort + the reader's card in the grid's first slot, opened through
 * a grid morph), which left the form a button away and moved every card after hydration.
 */
const OwnCaptureBand: React.VFC<OwnCaptureBandProps> = ({
  categoryName,
  categoryNoun,
  gridNumbers,
  questionCategory = null,
}) => {
  const { declared, declare, enrich } = useDeclaredProduct()
  const product = useMemo(() => {
    if (!declared?.nodeId) {
      return null
    }
    return { nodeId: declared.nodeId, name: declared.name, href: declared.href }
  }, [declared?.nodeId, declared?.name, declared?.href])
  const trackOwner = useOwnerTrack(product)
  const trailingSlash = publicConfig('useTrailingSlash')
  const noun = categoryNounOf(categoryName, categoryNoun)

  const [resolveError, setResolveError] = useState<string | null>(null)
  // One slim strip (2026-10-17): on a phone the search shows once "Add the one you own" is tapped
  // (`adding`, the row's one tap target).
  const [adding, setAdding] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (adding) {
      inputRef.current?.focus()
    }
  }, [adding])
  // Already left a note on the declared product? Show it instead of asking again.
  const {
    me,
    ownNote,
    claimed,
    pending: ownPending,
    applyResult,
    applyClaim,
    clearDevice,
  } = useOwnNote(declared?.nodeId ?? null)
  // "Own another? Switch" pressed while a note existed: the ask says the note stays up.
  const [switchedFrom, setSwitchedFrom] = useState<string | null>(null)

  // One render after mount `declared` is the reader's real answer (the provider reads
  // localStorage in an effect, after this one) — as in `GuideCapture`; before 2026-09-18 the band
  // marked itself shown on mount, so a returning declared reader was a band impression and the
  // band counted every category page load, on screen or not.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  // On the ask row only (2026-10-17): the question row under it must not move this impression's
  // on-screen ratio — the row is the same box the whole band was before it.
  const bandRef = useRef<HTMLDivElement>(null)
  const bandAsking = mounted && !declared
  const bandSeen = useOnScreenOnce(bandRef, bandAsking)
  const bandShown = useRef(false)
  useEffect(() => {
    if (bandAsking && bandSeen && !bandShown.current) {
      bandShown.current = true
      trackOwner('ask_shown', { location: 'band' })
    }
  }, [bandAsking, bandSeen, trackOwner])

  // A grid card for the declared product carries axes/battery/specs/lab id the typeahead
  // didn't (and fresher ones than a stored declaration from an earlier visit).
  useEffect(() => {
    if (!declared?.nodeId) {
      return
    }
    const fresh = gridNumbers.get(declared.nodeId)
    if (!fresh) {
      return
    }
    const wants =
      (fresh.axes && !declared.axes) ||
      (fresh.batteryHours !== null && declared.batteryHours === null) ||
      (typeof fresh.clearbuyProductId === 'number' &&
        typeof declared.clearbuyProductId !== 'number') ||
      (fresh.compareSpecs && !declared.compareSpecs)
    if (wants) {
      enrich(fresh)
    }
  }, [declared, gridNumbers, enrich])

  // Owner counts for the strip's question: the grid card when the product is in it, else what
  // the declaration's resolve said, else one cheap public read (`useDeclaredOwners`).
  const gridEntry = declared?.nodeId ? gridNumbers.get(declared.nodeId) : undefined
  const declaredOwners = useDeclaredOwners(declared?.nodeId ?? null, Boolean(gridEntry))
  const ownersSummary = gridEntry ? ownersFromCard(gridEntry.cardOwners) : declaredOwners.owners

  // One Review Writer (P1): with the flag on for `strip`, Yes / No link the community writer
  // with the answer, and the form is never warmed here (an edit from the own card still opens
  // it in place).
  const handoff = useWriterHandoff('strip', declared?.href)
  // The sign-in gate (slice D): the strip's Yes / No body says it, inline or handed off (the
  // writer is gated too), and the band under it is gated. Declaring is not a publish: the ask
  // and the compare stay as they are.
  const gated = signInGateFor('category')
  // The form under the strip (`useNoteBand`): warmed on idle once an ask can be made.
  const band = useNoteBand(product && turnstileConfigured() && !handoff ? product.nodeId : null)
  const { bandOpen } = band

  const pending = usePendingDeclare(declare, trailingSlash)
  // The grid already knows this product (same slug) → its node id + full numbers, no resolve.
  const inGrid = (slug: string | null): [string, GridNumbers] | undefined => {
    if (!slug) {
      return undefined
    }
    return [...gridNumbers].find(([, entry]) => entry.slug === slug)
  }

  const pick = async (hit: DealHuntSuggestHit): Promise<void> => {
    setResolveError(null)
    setSwitchedFrom(null)
    const seed = seedFromHit(hit)
    try {
      // Outside the grid: the one resolve, so every card can compare against a pair from another
      // category (ticket §B) — the strip shows at once and fills in when it answers.
      const known = inGrid(seed.slug)
      let next
      if (known) {
        next = declaredFromSeed(seed, known[0], known[1], trailingSlash)
      } else {
        const resolved = await pending.start(seed)
        if (!resolved) {
          return
        }
        next = resolved.declared
        declaredOwners.setResolved(resolved.owners)
      }
      declare(next)
      // Proposal 5: the declare is the denominator of every band rate (#71).
      trackOwner('declared', {
        location: 'band',
        target_product_id: next.nodeId,
        target_product_name: seed.name,
      })
      if (!next.nodeId) {
        setResolveError(GUIDE_COPY.errors.noPage)
      }
    } catch {
      setResolveError(GUIDE_COPY.errors.lookup)
    }
  }

  // The search box, its debounce, its keys and its outside-click close (`own-typeahead`); a
  // result the reader rests on starts its resolve, unless the grid already has it.
  const typeahead = useOwnTypeahead(
    hit => void pick(hit),
    hit => {
      const seed = seedFromHit(hit)
      if (!inGrid(seed.slug)) {
        prefetchDeclaration(seed)
      }
    }
  )

  const onResult = (result: OwnerPublishResponse): void => applyResult(result)
  // From the band and from the own card alike: `/me` and the note (#120).
  const onClaimedHere = (r: OwnerClaimResponse): void => {
    applyClaim(r)
  }

  // No Turnstile site key = no publish can succeed: the compare still works, the ask is not
  // made (see `turnstileConfigured`). The strip only exists after hydration (the declaration is
  // read from localStorage); on a browser with a device the ask waits for `/me`, like the Owners
  // section — never a question that turns into the reader's note.
  const askVisible = ownerAskVisible({
    configured: turnstileConfigured(),
    hydrated: true,
    hasOwnNote: ownNote !== null,
    bandOpen,
    ownPending,
  })
  const showAsk = Boolean(product && askVisible)
  // The question's own impression, once per declared product and only on screen. `strip` from
  // 2026-09-18; `tile` before it (the invite seated in the grid) — never read a rate across.
  const stripRef = useRef<HTMLElement>(null)
  const stripSeen = useOnScreenOnce(stripRef, showAsk)
  const stripShownFor = useRef<string | null>(null)
  useEffect(() => {
    const nodeId = declared?.nodeId ?? null
    if (showAsk && stripSeen && nodeId && stripShownFor.current !== nodeId) {
      stripShownFor.current = nodeId
      trackOwner('ask_shown', { location: 'strip' })
    }
  }, [showAsk, stripSeen, declared?.nodeId, trackOwner])

  // ── declared: the reference strip, in place (2026-09-18) ─────────────────────
  if (declared) {
    const clear = (): void => {
      pending.cancel()
      setSwitchedFrom(ownNote ? declared.name : null)
      declare(null)
      band.close()
    }
    // "Change" clears and opens the search for another (focused, the row's own `adding`); "Clear"
    // only clears.
    const change = (): void => {
      clear()
      setAdding(true)
    }
    return (
      <section
        className={styles.declared}
        aria-label={GUIDE_COPY.strip.lead(declared.name)}
        ref={stripRef}
      >
        <DeclaredStrip
          declared={declared}
          lead={GUIDE_COPY.strip.lead(declared.name)}
          body={categoryStripBody(categoryName, categoryNoun)}
          statusLabel={ownNote ? ownStatusLabel(ownNote.status) : null}
          clearLabel={ownNote ? SWITCH_LABEL : UNDECLARE_LABEL}
          onClear={change}
          onRemove={clear}
          ask={
            showAsk
              ? {
                  question: GUIDE_COPY.strip.question,
                  body: (gated ? GATED_GUIDE_COPY : GUIDE_COPY).strip.questionBody(
                    ownersSummary?.count ?? 0,
                    handoff?.hint
                  ),
                  answers: GUIDE_COPY.strip.answers,
                  onAnswer: buyAgain => band.open(false, buyAgain),
                  hrefFor: handoff?.href,
                  linkProps: handoff?.linkProps,
                }
              : null
          }
        />
        {/* "Something to ask other <noun> owners?" — under the strip's buy-again row, never
            inside `DeclaredStrip` (one component on both capture surfaces, 35b12564). */}
        {questionCategory && !bandOpen && (
          <CategoryQuestion
            categoryNodeId={questionCategory.nodeId}
            categoryFullSlug={questionCategory.fullSlug}
            noun={noun}
          />
        )}
        {/* The author's note, where the ask was: Edit, "See it on the product page" and — on a
            live anonymous note — the keep-it-yours sign-in (`OwnNoteCard` holds its own
            `useKeepSurface`, Directive 3b903d15). No Delete: the full control surface stays on
            the product page, as on a guide. */}
        {product && ownNote && !bandOpen && (
          <div className={styles.ownNote}>
            <OwnNoteCard
              note={ownNote}
              claimed={claimed}
              trackOwner={trackOwner}
              productHref={declared.href}
              location="own_note"
              // The author's readers row, as on the product page (Guillaume, 2026-09-22).
              views={ownViewsFromMe(me, declared.nodeId)}
              onEdit={() => band.open(true)}
              onClaimed={onClaimedHere}
            />
          </div>
        )}
        {product && bandOpen && band.OwnerNoteBand && (
          // `band_opened` says which surface opened it: the strip's question (`strip`) or an
          // edit from the reader's own card (`own_note`, not an ask converting).
          <SignInGateContext.Provider value={gated}>
            <band.OwnerNoteBand
              product={product}
              existing={band.editing ? ownNote : null}
              seedBuyAgain={band.seedBuyAgain}
              location={band.editing ? 'own_note' : 'strip'}
              trackOwner={trackOwner}
              onClose={band.close}
              onResult={onResult}
              me={me}
              onDeviceForgotten={clearDevice}
              onClaimed={onClaimedHere}
              draftKey={bandDraftKey(product.nodeId)}
            />
          </SignInGateContext.Provider>
        )}
        {resolveError && <p className={styles.error}>{resolveError}</p>}
      </section>
    )
  }

  // ── the ask: one slim strip (2026-10-17, trimmed 2026-10-27) ─────────────
  // Desktop: one row — the lead and the search — where the two-row band ("Own one already?" +
  // "Ask people who own <noun>", ~120px more) stood. Phone: one tap target, the search under it
  // once Add is tapped. Same box, same place: `ask_shown{location: band}` reads across the
  // changes. Gone 2026-10-27 (Guillaume): the row's "Ask owners a question →" (the declared
  // strip keeps its "Ask a question →" line) and the "Sign-in needed to publish." fine print —
  // declaring is not a publish; the Yes / No body still says it before any writing.
  const { showDropdown, results, highlight, loading, trimmed } = typeahead
  return (
    <section className={styles.band} aria-label={CATEGORY_STRIP.lead(noun)}>
      <div className={clsx(styles.row, { [styles.rowAdding]: adding })} ref={bandRef}>
        {/* The words, desktop only: the phone's button carries the ask on its own. */}
        <p className={styles.lead}>
          <strong className={styles.leadStrong}>{CATEGORY_STRIP.lead(noun)}</strong>
          {CATEGORY_STRIP.body}
        </p>
        <button
          type="button"
          className={styles.addTap}
          aria-expanded={adding}
          aria-controls="own-capture-search"
          onClick={() => setAdding(open => !open)}
        >
          {CATEGORY_STRIP.addTap}
        </button>
        <div ref={typeahead.rootRef} className={styles.search} id="own-capture-search">
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            role="combobox"
            aria-expanded={showDropdown}
            aria-controls="own-capture-results"
            aria-label={CATEGORY_STRIP.inputLabel}
            placeholder={CATEGORY_STRIP.placeholder}
            autoComplete="off"
            value={typeahead.query}
            onFocus={() => typeahead.setOpen(true)}
            onChange={e => {
              typeahead.setQuery(e.target.value)
              typeahead.setOpen(true)
            }}
            onKeyDown={typeahead.onKeyDown}
          />
          {showDropdown && (
            <div className={styles.dropdown} id="own-capture-results" role="listbox">
              {results.map((hit, i) => (
                <button
                  key={hit.objectID}
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  className={clsx(styles.hit, { [styles.hitOn]: i === highlight })}
                  onMouseEnter={() => typeahead.setHighlight(i)}
                  onClick={() => typeahead.select(hit)}
                >
                  <span className={styles.hitThumb}>
                    {(hit.cloudinaryImageUrl || hit.imageUrl) && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={hit.cloudinaryImageUrl || hit.imageUrl || ''}
                        alt=""
                        loading="lazy"
                      />
                    )}
                  </span>
                  <span className={styles.hitName}>
                    {brandedTitle(hit.brand, hit.canonicalName || hit.title)}
                  </span>
                </button>
              ))}
              {!results.length && (
                <p className={styles.notice}>
                  {loading ? 'Searching…' : `Nothing matches “${trimmed}”.`}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      {switchedFrom && <p className={styles.switchLine}>{switchLine(switchedFrom)}</p>}
      {resolveError && <p className={styles.error}>{resolveError}</p>}
    </section>
  )
}

export default OwnCaptureBand
